import { useCallback, useEffect, useState } from 'react';

import { OAUTH_CONNECTION_PARAM, OAUTH_ERROR_PARAM } from '@app/common';
import type { SocialPlatform } from '@app/common';
import {
  disconnectSocialAccount,
  finalizeConnect,
  getPendingConnection,
  initializeConnect,
  listSocialAccounts,
} from '../api/oauth.api';
import type { OAuthPendingConnection, SocialAccountSummary } from '../api/oauth.api';
import { useAuth } from '../context/AuthContext';
import { useBrandsData } from './useBrandsData';
import { env } from '../config/env';

/**
 * Mirrors the backend's `[oauth]` trace so one browser console and one server
 * terminal read as a single timeline.
 *
 * Dev only — `env.isDev` rather than a bare NODE_ENV check, because every
 * `import.meta.env` read in this app goes through config/env.ts. Nothing secret
 * is printed: the connection id is a short-lived handle already sitting in the
 * address bar, and no token ever reaches the browser at all.
 */
const trace = (step: string, detail?: Record<string, unknown>): void => {
  if (!env.isDev) return;
  console.log(`[oauth] ${step}`, detail ?? '');
};

/**
 * Drives the three-phase connect flow from the browser's side.
 *
 * Phase 2 has no client call at all: the provider redirects to the backend,
 * which redirects back here carrying `?connection=<id>`. So the "did we just come
 * back from Facebook" check is a URL read on mount, not a request — and the
 * parameter is stripped immediately afterwards, because a reload must not re-open
 * the picker for a connection that has already been finalized.
 */
export function useConnectedAccounts() {
  const { user } = useAuth();
  const { brands, loading: brandsLoading } = useBrandsData();

  const [accounts, setAccounts] = useState<SocialAccountSummary[]>([]);
  const [pending, setPending] = useState<OAuthPendingConnection | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busyPlatform, setBusyPlatform] = useState<SocialPlatform | null>(null);
  const [nonce, setNonce] = useState(0);

  // The brand every connection is filed under. There is no brand picker on this
  // screen yet, so the first brand is it — and with no brand at all there is
  // nothing to connect to, which the page renders as its empty state.
  const brandId = brands[0]?.id ?? null;

  const refresh = useCallback(() => setNonce((n) => n + 1), []);

  useEffect(() => {
    if (!user) {
      setAccounts([]);
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);

    void (async () => {
      try {
        const data = await listSocialAccounts();
        trace('accounts loaded', { count: data.length });
        if (!cancelled) setAccounts(data);
      } catch (err: unknown) {
        if (!cancelled) setError((err as { message?: string }).message ?? 'Failed to load accounts');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [user, nonce]);

  // Coming back from the provider.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const connectionId = params.get(OAUTH_CONNECTION_PARAM);
    const oauthError = params.get(OAUTH_ERROR_PARAM);

    trace('3/returned from provider', {
      url: window.location.href,
      connectionId: connectionId ?? '<none>',
      oauthError: oauthError ?? '<none>',
    });

    // Neither parameter: an ordinary visit, not a return trip.
    if (!connectionId && !oauthError) return;

    // Strip before doing anything else: a refresh mid-picker would otherwise try
    // to reopen a connection that finalize has already consumed.
    params.delete(OAUTH_CONNECTION_PARAM);
    params.delete(OAUTH_ERROR_PARAM);
    const query = params.toString();
    window.history.replaceState({}, '', `${window.location.pathname}${query ? `?${query}` : ''}`);

    if (oauthError) {
      trace('3/callback failed — finalize will NOT run', { code: oauthError });
      setError(oauthError);
      return;
    }

    if (!connectionId) return;

    void (async () => {
      try {
        const connection = await getPendingConnection(connectionId);
        trace('3/pending connection loaded — opening picker', {
          platform: connection.platform,
          targets: connection.targets.map((t) => t.name),
        });
        setPending(connection);
      } catch (err: unknown) {
        trace('3/pending connection could not be read', { err });
        setError((err as { message?: string }).message ?? 'That connection expired.');
      }
    })();
  }, []);

  /** Phase 1 — ends in a full-page navigation, so nothing after it runs. */
  const connect = useCallback(
    async (platform: SocialPlatform) => {
      trace('1/connect clicked', { platform, brandId: brandId ?? '<none>' });

      if (!brandId) {
        trace('1/connect aborted — no brand to file the connection under');
        return;
      }
      setBusyPlatform(platform);
      setError(null);
      try {
        const { authorizeUrl } = await initializeConnect(platform, {
          brandId,
          redirectTo: '/accounts',
        });
        // The redirect_uri inside this URL is what the provider compares against
        // its registered list — the usual thing to eyeball when consent fails.
        trace('1/initialize ok — leaving for the provider', { authorizeUrl });
        window.location.assign(authorizeUrl);
      } catch (err: unknown) {
        trace('1/initialize failed', { err });
        setError((err as { message?: string }).message ?? 'Could not start the connection.');
        setBusyPlatform(null);
      }
    },
    [brandId]
  );

  /** Phase 3 — the user picked which discovered accounts to keep. */
  const finalize = useCallback(
    async (targetIds: string[]) => {
      if (!pending) return;
      trace('4/finalize sending', { connectionId: pending.id, targetIds });
      try {
        const saved = await finalizeConnect(pending.id, { targetIds });
        trace('4/finalize ok', { connected: saved.map((a) => a.externalName ?? a.externalId) });
        setPending(null);
        refresh();
      } catch (err: unknown) {
        trace('4/finalize failed', { err });
        setError((err as { message?: string }).message ?? 'Could not connect those accounts.');
        throw err;
      }
    },
    [pending, refresh]
  );

  const disconnect = useCallback(
    async (accountId: string) => {
      setError(null);
      try {
        await disconnectSocialAccount(accountId);
        refresh();
      } catch (err: unknown) {
        setError((err as { message?: string }).message ?? 'Could not disconnect that account.');
      }
    },
    [refresh]
  );

  return {
    accounts,
    pending,
    dismissPending: () => setPending(null),
    brandId,
    loading: loading || brandsLoading,
    error,
    clearError: () => setError(null),
    busyPlatform,
    connect,
    finalize,
    disconnect,
  };
}
